import { inject, Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RBCheck } from '@lfw/roesberg';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, ReplaySubject, Subscription } from 'rxjs';
import {
  routeAbout,
  routeLogoff,
  routeManual,
  routeOfflineOrders,
  routeOrders,
  routeProjects,
  routeSettings,
  routeTemporaryOfflineOrders
} from '../liveformsweb.routes';
import { NetworkService } from '../network.service';
import { OfflineFormsClient } from '../orders/offline-orders/offline-forms.client';
import { ProjectsService } from '../projects/projects.service';
import { MenuCommand } from '../shared/models/menu-command';
import { SettingsService } from '../shared/settings/settings.service';

@Injectable({
  providedIn: 'root'
})
export class MenuService implements OnDestroy {
  private subscription: Subscription = new Subscription();

  // ReplaySubject(1) ensures late subscribers (e.g. after @if re-creates the menu) immediately receive the last emission.
  rebuilt$ = new ReplaySubject<void>(1);

  cmdProject?: MenuCommand;
  cmdOrders?: MenuCommand;
  cmdTemporaryOfflineOrders?: MenuCommand;
  cmdOfflineOrders?: MenuCommand;
  cmdSearch?: MenuCommand;
  cmdSettings?: MenuCommand;
  cmdSignOff?: MenuCommand;
  cmdUser?: MenuCommand;
  cmdDefaultHelp?: MenuCommand;
  cmdCustomHelp?: MenuCommand;
  cmdAbout?: MenuCommand;

  navigationAllowed = true;

  private readonly projectService = inject(ProjectsService);
  private readonly settingsService = inject(SettingsService);
  private readonly offlineFormsIndexDbClient = inject(OfflineFormsClient);
  private readonly translateService = inject(TranslateService);
  private readonly networkService = inject(NetworkService);

  constructor() {
    this.buildCommands();
    this.subscription.add(
      this.projectService.projectChange.subscribe(() => {
        this.buildCommands();
      })
    );
    this.subscription.add(
      this.settingsService.settingsLoaded.subscribe(() => {
        this.buildCommands();
      })
    );
    this.offlineFormsIndexDbClient.offlineOrders$.pipe(takeUntilDestroyed()).subscribe(() => this.buildCommands());
    this.networkService.state$.pipe(takeUntilDestroyed()).subscribe(() => this.buildCommands());
  }

  // wird nicht aufgerufen !
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  async buildCommands(): Promise<void> {
    const networkState = await firstValueFrom(this.networkService.state$);
    const project = this.projectService.currentProjectId;

    if (networkState === 'online') {
      this.cmdProject = this.newCommand('topic', routeProjects, false);
      this.cmdOrders = this.newCommand('article', `${routeOrders}/${project}`, true);
    } else {
      this.cmdProject = undefined;
      this.cmdOrders = undefined;
    }

    this.cmdTemporaryOfflineOrders =
      (await this.offlineFormsIndexDbClient.temporaryOfflineOrdersCount()) === 0
        ? undefined
        : this.newCommand('android_wifi_3_bar_question', `${routeTemporaryOfflineOrders}`, false);

    this.cmdOfflineOrders =
      (await this.offlineFormsIndexDbClient.offlineOrdersCount()) === 0
        ? undefined
        : this.newCommand('cloud_off', `${routeOfflineOrders}`, false);

    // this.cmdSearch = this.newCommand('search', `${routeSearch}/${project}`, true);
    /*

    this.cmdLoops = this.newCommand('home', `${routeBasicNavigation}/${wd}`, true);
    this.cmdCustomNavigation = this.newCommand('format_align_justify', `${routeCustomNavigation}/${wd}`, true);
    this.cmdInfoChanged = this.newCommand('published_with_changes', `${routeInfoChanged}/${wd}`, true);
    this.cmdInfoHistory = this.newCommand('history_toggle_off', `${routeInfoHistory}/${wd}`, true);
    this.cmdInfoWebDoku = this.newCommand('analytics', `${routeInfoWebDoku}/${wd}`, true);

     */

    if (networkState === 'online') {
      this.cmdSettings = this.newCommand('settings', routeSettings);
      this.cmdSignOff = this.newCommand('power_settings_new', routeLogoff);
      this.cmdUser = this.newCommand('person', 'user'); /* not a real route ! */
    } else {
      this.cmdSettings = undefined;
      this.cmdSignOff = undefined;
      this.cmdUser = undefined;
    }

    // Built regardless of network state: the app version comes from the bundle, and the page is most
    // useful when something is wrong. Offline it shows the last API version AppConfig loaded.
    this.cmdAbout = this.newCommand('info_outline', routeAbout);

    const sett = this.settingsService.settings.helpSettings;

    if (sett.defaultHelpFileVisible) {
      this.cmdDefaultHelp = this.newCommand('help', routeManual);
    }

    if (sett.customHelpFileVisible()) {
      this.cmdCustomHelp = new MenuCommand(this.translateService);
      this.cmdCustomHelp.icon = 'info';
      this.cmdCustomHelp.route = sett.customHelpFile;
      this.cmdCustomHelp.customText = sett.customHelpFileText;
      this.cmdCustomHelp.isExternalRoute = true;
    }

    this.rebuilt$.next();
  }

  newCommand(icon: string, route: string, needProject = false): MenuCommand {
    const cmd = new MenuCommand(this.translateService);
    cmd.icon = icon;
    cmd.route = route;
    cmd.transId = this.splitString(route);
    cmd.needProject = needProject;
    return cmd;
  }

  splitString(text: string): string {
    if (RBCheck.isNullOrEmpty(text)) {
      return '';
    }
    const parts = text.split('/');
    if (parts.length <= 1) {
      return text;
    }
    return parts[0];
  }

  hideNavigation(): void {
    this.navigationAllowed = false;
  }

  showNavigation(): void {
    this.navigationAllowed = true;
  }
}
